import React, { useRef } from 'react';
import { Camera, Upload } from 'lucide-react';

type ImageSourceButtonsProps = {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
  /** Allow picking multiple images from gallery. Camera always captures one at a time. */
  multiple?: boolean;
  uploadLabel?: string;
  cameraLabel?: string;
  className?: string;
  size?: 'sm' | 'md';
  /** `user` = front/selfie camera; `environment` = rear camera (default). */
  cameraFacing?: 'user' | 'environment';
};

/**
 * Two explicit image sources: gallery upload and device camera.
 * Used anywhere we need "Upload" vs "Take photo" (applicant photos, stock images, logo).
 */
export function ImageSourceButtons({
  onFiles,
  disabled = false,
  multiple = false,
  uploadLabel = 'Upload',
  cameraLabel = 'Take photo',
  className = '',
  size = 'md',
  cameraFacing = 'environment',
}: ImageSourceButtonsProps) {
  const uploadRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);

  const btnClass =
    size === 'sm'
      ? 'app-btn-secondary inline-flex items-center gap-1 text-xs px-2.5 py-1.5'
      : 'app-btn-secondary inline-flex items-center gap-1.5 text-sm';

  const iconClass = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4';

  const pick = (list: FileList | null | undefined, input: HTMLInputElement | null) => {
    if (!list?.length) return;
    onFiles(Array.from(list));
    if (input) input.value = '';
  };

  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      <button type="button" className={btnClass} disabled={disabled} onClick={() => uploadRef.current?.click()}>
        <Upload className={iconClass} />
        {uploadLabel}
      </button>
      <button type="button" className={btnClass} disabled={disabled} onClick={() => cameraRef.current?.click()}>
        <Camera className={iconClass} />
        {cameraLabel}
      </button>

      <input
        ref={uploadRef}
        type="file"
        accept="image/*"
        multiple={multiple}
        className="hidden"
        disabled={disabled}
        onChange={(e) => pick(e.target.files, e.target)}
      />
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture={cameraFacing}
        className="hidden"
        disabled={disabled}
        onChange={(e) => pick(e.target.files, e.target)}
      />
    </div>
  );
}
